import { useEffect, useRef, useState } from 'react';
import { useReportPrompt } from './useReportPrompt';

/**
 * Owns whether the end-of-test wizard is open, for the two actions that conclude a test on
 * screen: End from the header and Deploy from the Statistics table. Both call `offer`, which
 * asks the server whether anything has been answered yet and opens the wizard only when not.
 *
 * The check is in flight for a moment after End or Deploy, and the header used to open the
 * wizard for whatever experiment was on screen when it resolved. So the open state is the id
 * of the experiment the offer was made for, and an offer that resolves once the screen has
 * moved on to another experiment is dropped rather than kept: the details screen is one route
 * element for every experiment, so its state outlives the id, and a kept offer would open the
 * wizard unasked the next time that experiment came back.
 */
export const useReportWizard = (experimentId: number) => {
  const shouldPrompt = useReportPrompt(experimentId);
  const [openFor, setOpenFor] = useState<number | null>(null);
  const onScreen = useRef(experimentId);

  useEffect(() => {
    onScreen.current = experimentId;
  }, [experimentId]);

  const offer = async () => {
    const wanted = await shouldPrompt();
    if (wanted && onScreen.current === experimentId) setOpenFor(experimentId);
  };

  const close = () => setOpenFor(null);

  return { isOpen: openFor === experimentId, offer, close };
};
